"use client";

import type { CheckoutScreeningQuestion } from "@fm/api/screening-questions-checkout";
import { RadioGroup, RadioGroupItem } from "@fm/ui";
import type { ScreeningErrors, ScreeningValues } from "../lib/screening";

export interface ScreeningQuestionsSectionProps {
  fields: CheckoutScreeningQuestion[];
  values: ScreeningValues;
  errors: ScreeningErrors;
  onChange: (questionId: number, value: string) => void;
  disabled?: boolean;
  className?: string;
}

const INPUT_CLASS =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50";

/**
 * The fleet's active custom booking questions for the current flow
 * (`instant` or `request`), rendered from the dynamic catalog
 * (`GET /v1/screening-questions/checkout`) rather than a fixed set. Purely
 * presentational; `lib/screening` owns which questions exist (via the
 * `useScreeningQuestionsCheckout` hook upstream), their validation and the
 * request payload.
 */
export function ScreeningQuestionsSection({
  fields,
  values,
  errors,
  onChange,
  disabled = false,
  className = "",
}: ScreeningQuestionsSectionProps) {
  if (fields.length === 0) return null;

  return (
    <div
      className={`space-y-4 ${className}`}
      data-testid="screening-questions-section"
    >
      {fields.map((field) => {
        const key = String(field.id);
        const inputId = `screening-${field.id}`;
        const errorId = `${inputId}-error`;
        const error = errors[key];
        const value = values[key] ?? "";
        const describedBy = error ? errorId : undefined;

        return (
          <div key={field.id}>
            {field.type === "radio" ? (
              <fieldset aria-describedby={describedBy}>
                <legend className="mb-1 text-sm font-medium text-gray-900">
                  {field.label}
                  {field.required ? " *" : ""}
                </legend>
                <RadioGroup
                  value={value}
                  onValueChange={(next) => onChange(field.id, next)}
                  aria-invalid={!!error}
                  aria-describedby={describedBy}
                  className="flex flex-col gap-2"
                >
                  {field.options.map((option: string) => (
                    <label
                      key={option}
                      htmlFor={`${inputId}-${option}`}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <RadioGroupItem
                        value={option}
                        id={`${inputId}-${option}`}
                        disabled={disabled}
                      />
                      {option}
                    </label>
                  ))}
                </RadioGroup>
              </fieldset>
            ) : (
              <>
                <label
                  htmlFor={inputId}
                  className="mb-1 block text-sm font-medium text-gray-900"
                >
                  {field.label}
                  {field.required ? " *" : ""}
                </label>
                {field.type === "textarea" ? (
                  <textarea
                    id={inputId}
                    rows={4}
                    value={value}
                    disabled={disabled}
                    aria-invalid={!!error}
                    aria-describedby={describedBy}
                    onChange={(e) => onChange(field.id, e.target.value)}
                    className={INPUT_CLASS}
                  />
                ) : (
                  <input
                    id={inputId}
                    type={field.type === "number" ? "number" : "text"}
                    value={value}
                    disabled={disabled}
                    aria-invalid={!!error}
                    aria-describedby={describedBy}
                    onChange={(e) => onChange(field.id, e.target.value)}
                    className={INPUT_CLASS}
                  />
                )}
              </>
            )}
            {error && (
              <p
                id={errorId}
                role="alert"
                className="mt-1 text-sm text-red-600"
              >
                {error}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
